import { BellOff, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { hostFollowApi, type FollowedHost } from '../services/hostFollowApi';

import Card from './ui/Card';

/**
 * "Hosts I follow" in the guest dashboard: the logged-in counterpart to
 * the one-click unfollow in the emails, so a user can review and cancel
 * every follow in one place.
 */
export default function FollowedHostsTab() {
  const { t } = useTranslation('hostFollow');
  const [hosts, setHosts] = useState<FollowedHost[]>([]);
  const [loading, setLoading] = useState(true);
  // Error codes, not translated strings: keeping t out of the load callback
  // means a new t identity on re-render cannot retrigger the fetch.
  const [error, setError] = useState<'load' | 'action' | null>(null);
  // A set, not a single id: the other rows stay clickable while one request is
  // in flight, so several can legitimately overlap.
  const [busyHostIds, setBusyHostIds] = useState<ReadonlySet<string>>(new Set());

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    hostFollowApi
      .listMine()
      .then(setHosts)
      .catch(() => setError('load'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const errorMessage = error === 'load' ? t('manage.loadError') : error ? t('actionError') : '';

  const unfollow = async (hostId: string) => {
    const index = hosts.findIndex((host) => host.hostId === hostId);
    if (index === -1) return;
    const removed = hosts[index];

    setBusyHostIds((current) => new Set(current).add(hostId));
    setError(null);
    // Optimistic: the row disappears, and comes back if the call fails.
    setHosts((current) => current.filter((host) => host.hostId !== hostId));
    try {
      await hostFollowApi.unfollow(hostId);
    } catch {
      // Put back only this row. Restoring a whole snapshot would resurrect any
      // other host that was successfully unfollowed while this call was out.
      setHosts((current) => {
        if (current.some((host) => host.hostId === hostId)) return current;
        const next = [...current];
        next.splice(Math.min(index, next.length), 0, removed);
        return next;
      });
      setError('action');
    } finally {
      setBusyHostIds((current) => {
        const next = new Set(current);
        next.delete(hostId);
        return next;
      });
    }
  };

  if (loading) {
    return (
      <Card padding="lg">
        <div className="py-8 text-center">
          <Loader2 className="mx-auto mb-4 h-8 w-8 animate-spin text-primary" />
        </div>
      </Card>
    );
  }

  if (hosts.length === 0) {
    return (
      <Card padding="lg">
        <div className="py-8 text-center">
          <p className="mb-4 text-gray-600">{errorMessage || t('manage.empty')}</p>
          <Link
            to="/activities"
            className="font-semibold text-primary transition-colors hover:text-primary/80"
          >
            {t('manage.emptyCta')}
          </Link>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {errorMessage && <p className="text-sm text-red-600">{errorMessage}</p>}
      {hosts.map((host) => (
        <Card key={host.hostId} padding="sm">
          {/* One row at every width. Stacking on narrow screens stretched the
              button to the full card, which is what made it read as a slab with
              its label stranded on the left. */}
          <div className="flex items-center gap-3">
            {host.avatarUrl ? (
              <img
                src={host.avatarUrl}
                alt={host.displayName}
                className="h-10 w-10 flex-shrink-0 rounded-full object-cover"
              />
            ) : (
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary/10">
                <span className="font-bold text-primary">{host.displayName.charAt(0)}</span>
              </div>
            )}
            <Link
              to={`/activities?hostId=${host.hostId}`}
              className="min-w-0 flex-1 truncate font-semibold text-gray-900 hover:text-primary"
            >
              {host.displayName}
            </Link>
            <button
              type="button"
              onClick={() => unfollow(host.hostId)}
              disabled={busyHostIds.has(host.hostId)}
              className="inline-flex flex-shrink-0 items-center justify-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-60"
            >
              {busyHostIds.has(host.hostId) ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <BellOff className="h-4 w-4" />
              )}
              {t('manage.unfollow')}
            </button>
          </div>
        </Card>
      ))}
    </div>
  );
}
